import React, { useMemo } from 'react';

const DEFAULT_MARGIN = { left: 46, right: 12, top: 10, bottom: 34 };

/** Auto domain from point x/y values with padding. */
export function scatterDomainFromPoints(points, padRatio = 0.06) {
  if (!points.length) return [0, 1];
  const vals = [];
  for (const p of points) {
    if (p.x != null) vals.push(p.x);
    if (p.y != null) vals.push(p.y);
  }
  if (!vals.length) return [0, 1];
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  const pad = Math.max(0.005, (max - min) * padRatio);
  return [min - pad, max + pad];
}

/**
 * Generic 2D scatter plot in SVG. Presentational only: the host supplies points,
 * optional reference lines, axis labels, and a legend.
 *
 * @param {Object[]} points — { x, y, color?, fillOpacity?, radius?, stroke?, strokeWidth?, title?, key? }
 * @param {[number, number]} [domain] — shared [min, max] for both axes (auto from points if omitted)
 * @param {Object[]} [verticalLines] — { value, color?, strokeDasharray? }
 * @param {Object[]} [horizontalLines] — { value, color?, strokeDasharray? }
 */
export default function ScatterPlot2D({
  points = [],
  width = 420,
  height = 340,
  margin = DEFAULT_MARGIN,
  domain: domainProp = null,
  xLabel = '',
  yLabel = '',
  showDiagonal = false,
  verticalLines = [],
  horizontalLines = [],
  caption = null,
  legend = null,
  className = '',
  style = null,
}) {
  const m = { ...DEFAULT_MARGIN, ...margin };
  const [min, max] = useMemo(
    () => (domainProp || scatterDomainFromPoints(points)),
    [points, domainProp],
  );
  const range = max - min || 1;

  const sx = (v) => m.left + ((v - min) / range) * (width - m.left - m.right);
  const sy = (v) => height - m.bottom - ((v - min) / range) * (height - m.top - m.bottom);
  const ticks = [0, 1, 2, 3, 4].map((i) => min + (i * range) / 4);

  return (
    <div className={className} style={style}>
      {caption}
      <svg width={width} height={height} style={{ background: '#fafafa', border: '1px solid #e5e7eb' }}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={sx(t)} y1={m.top} x2={sx(t)} y2={height - m.bottom} stroke="#eee" />
            <line x1={m.left} y1={sy(t)} x2={width - m.right} y2={sy(t)} stroke="#eee" />
            <text x={sx(t)} y={height - m.bottom + 13} fontSize="10" textAnchor="middle" fill="#666">
              {t.toFixed(3)}
            </text>
            <text x={m.left - 4} y={sy(t) + 3} fontSize="10" textAnchor="end" fill="#666">
              {t.toFixed(3)}
            </text>
          </g>
        ))}
        {showDiagonal ? (
          <line
            x1={sx(min)}
            y1={sy(min)}
            x2={sx(max)}
            y2={sy(max)}
            stroke="#cbd5e1"
            strokeDasharray="4 3"
          />
        ) : null}
        {verticalLines.map((line, i) => (
          Number.isFinite(line.value) && line.value >= min && line.value <= max ? (
            <line
              key={`v${i}`}
              x1={sx(line.value)}
              y1={m.top}
              x2={sx(line.value)}
              y2={height - m.bottom}
              stroke={line.color || '#f97316'}
              strokeDasharray={line.strokeDasharray || '5 3'}
            />
          ) : null
        ))}
        {horizontalLines.map((line, i) => (
          Number.isFinite(line.value) && line.value >= min && line.value <= max ? (
            <line
              key={`h${i}`}
              x1={m.left}
              y1={sy(line.value)}
              x2={width - m.right}
              y2={sy(line.value)}
              stroke={line.color || '#f97316'}
              strokeDasharray={line.strokeDasharray || '5 3'}
            />
          ) : null
        ))}
        {points.map((p, i) => (
          <circle
            key={p.key ?? i}
            cx={sx(p.x)}
            cy={sy(p.y)}
            r={p.radius ?? 3.5}
            fill={p.color || '#2563eb'}
            fillOpacity={p.fillOpacity ?? 0.55}
            stroke={p.stroke ?? '#fff'}
            strokeWidth={p.strokeWidth ?? 0.5}
          >
            {p.title ? <title>{p.title}</title> : null}
          </circle>
        ))}
        {xLabel ? (
          <text x={(m.left + width - m.right) / 2} y={height - 4} fontSize="11" textAnchor="middle" fill="#333">
            {xLabel}
          </text>
        ) : null}
        {yLabel ? (
          <text
            x={11}
            y={(m.top + height - m.bottom) / 2}
            fontSize="11"
            textAnchor="middle"
            fill="#333"
            transform={`rotate(-90 11 ${(m.top + height - m.bottom) / 2})`}
          >
            {yLabel}
          </text>
        ) : null}
      </svg>
      {legend}
    </div>
  );
}
